import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Calendar, Clock, BookOpen, Star, Info, CheckCircle } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ApiService from '../services/api';
import toast from 'react-hot-toast';

const AddEditMuhurat = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        date: '',
        day: '',
        startTime: '',
        endTime: '',
        endDate: '',
        description: '',
        nakshatra: '',
        tithi: '',
        paksha: '',
        month: '',
        isActive: true
    });

    useEffect(() => {
        if (isEditMode) {
            if (location.state?.muhurat) {
                const m = location.state.muhurat;
                setFormData({
                    ...m,
                    date: new Date(m.date).toISOString().split('T')[0],
                    endDate: m.endDate ? new Date(m.endDate).toISOString().split('T')[0] : ''
                });
            } else {
                fetchMuhuratDetails();
            }
        }
    }, [id]);

    const fetchMuhuratDetails = async () => {
        try {
            setLoading(true);
            const response = await ApiService.getMuhuratById(id);
            if (response.success) {
                const m = response.data;
                setFormData({
                    ...m,
                    date: new Date(m.date).toISOString().split('T')[0],
                    endDate: m.endDate ? new Date(m.endDate).toISOString().split('T')[0] : ''
                });
            }
        } catch (error) {
            console.error('Error fetching muhurat:', error);
            toast.error('Failed to fetch muhurat details');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Auto-fill day if date changes
        if (name === 'date' && value) {
            const date = new Date(value);
            const days = ['रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            setFormData(prev => ({
                ...prev,
                day: days[date.getDay()],
                month: months[date.getMonth()],
                date: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const data = {
                ...formData,
                date: new Date(formData.date),
                endDate: formData.endDate ? new Date(formData.endDate) : null
            };

            let response;
            if (isEditMode) {
                response = await ApiService.updateMuhurat(id, data);
            } else {
                response = await ApiService.createMuhurat(data);
            }

            if (response.success) {
                toast.success(`Muhurat ${isEditMode ? 'updated' : 'created'} successfully`);
                navigate('/muhurats');
            }
        } catch (error) {
            console.error('Error saving muhurat:', error);
            toast.error('Failed to save muhurat');
        } finally {
            setLoading(false);
        }
    };

    const daysList = ['सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार', 'रविवार'];
    const monthsList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const pakshaList = ['Shukla Paksha', 'Krishna Paksha'];

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/muhurats')}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft className="w-6 h-6 text-gray-600" />
                </button>
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                        {isEditMode ? 'Edit Muhurat' : 'Add New Muhurat'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Fill in the details for the auspicious wedding timing
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Core Details Card */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-6">
                    <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-indigo-600" />
                        <h2 className="text-xl font-bold text-gray-900">Timing & Date</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Primary Date *</label>
                            <input
                                type="date"
                                name="date"
                                required
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                value={formData.date}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Day *</label>
                            <select
                                name="day"
                                required
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all bg-white"
                                value={formData.day}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Day</option>
                                {daysList.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Start Time *</label>
                            <input
                                type="text"
                                name="startTime"
                                required
                                placeholder="e.g. 11:16 AM"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                value={formData.startTime}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">End Time *</label>
                            <input
                                type="text"
                                name="endTime"
                                required
                                placeholder="e.g. 06:53 AM"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                value={formData.endTime}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">End Date (Optional)</label>
                            <input
                                type="date"
                                name="endDate"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                value={formData.endDate}
                                onChange={handleInputChange}
                            />
                            <p className="text-xs text-gray-400 mt-1">If the muhurat crosses midnight</p>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Month</label>
                            <select
                                name="month"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all bg-white"
                                value={formData.month}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Month</option>
                                {monthsList.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Astrology Details Card */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-6">
                    <div className="flex items-center gap-2 mb-2">
                        <Star className="w-5 h-5 text-amber-500" />
                        <h2 className="text-xl font-bold text-gray-900">Astrology Details</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Nakshatra</label>
                            <input
                                type="text"
                                name="nakshatra"
                                placeholder="e.g. Rohini"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                value={formData.nakshatra}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Tithi</label>
                            <input
                                type="text"
                                name="tithi"
                                placeholder="e.g. Ekadashi"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                value={formData.tithi}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Paksha</label>
                            <select
                                name="paksha"
                                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all bg-white"
                                value={formData.paksha}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Paksha</option>
                                {pakshaList.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Description & Settings Card */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-6">
                    <div className="flex items-center gap-2 mb-2">
                        <Info className="w-5 h-5 text-blue-600" />
                        <h2 className="text-xl font-bold text-gray-900">Additional Information</h2>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                        <textarea
                            name="description"
                            rows={4}
                            placeholder="Enter auspicious details or notes..."
                            className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                            value={formData.description}
                            onChange={handleInputChange}
                        ></textarea>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl">
                        <input
                            type="checkbox"
                            id="isActive"
                            name="isActive"
                            className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                        />
                        <label htmlFor="isActive" className="text-sm font-bold text-gray-700 cursor-pointer">
                            Mark as Active (Visibility on Mobile App)
                        </label>
                    </div>
                </div>

                {/* Submit Actions */}
                <div className="flex gap-4 pb-12">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-indigo-100 transition-all disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : (
                            <>
                                <Save className="w-5 h-5" />
                                {isEditMode ? 'Update Muhurat' : 'Create Muhurat'}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/muhurats')}
                        className="px-8 py-4 rounded-2xl border border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition-all"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddEditMuhurat;
